import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { TokenStore } from '../auth/TokenStore';
import { decodeIdToken } from '../auth/ClaimsParser';
import { AuthService } from '../auth/AuthService';
import { startLogin } from '../flows/LoginFlow';
import type { LoginStartOptions } from '../flows/LoginFlow';
import type { IdTokenClaims } from '../auth/AuthTokenTypes';

export type AuthUser = {
  sub: string;
  email?: string;
  name?: string;
  pictureUrl?: string;
  issuer?: string;
  expiresAt?: number;
  rawClaims?: Record<string, unknown>;
};

export type AuthState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  /** Last auth error suitable for generic UI display */
  error?: { kind: 'NOT_AUTHORIZED' | 'SESSION_EXPIRED' | 'UNKNOWN'; message: string };
};

export type AuthActions = {
  login: (options?: LoginStartOptions) => Promise<void>;
  logout: (options?: { returnTo?: string }) => void;
  refreshFromStore: () => Promise<void>;
};

export type AuthContextValue = AuthState & AuthActions;

const initialState: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  user: null,
};

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_AUTHENTICATED'; payload: { user: AuthUser } }
  | { type: 'SET_UNAUTHENTICATED'; payload?: { error?: AuthState['error'] } }
  | { type: 'CLEAR_ERROR' };

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_AUTHENTICATED':
      return {
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        error: undefined,
      };
    case 'SET_UNAUTHENTICATED':
      return {
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload?.error,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: undefined };
    default:
      return state;
  }
}

function claimsToAuthUser(claims: IdTokenClaims): AuthUser {
  let name = claims.name;
  if (!name && (claims.given_name || claims.family_name)) {
    name = [claims.given_name, claims.family_name].filter(Boolean).join(' ');
  }
  return {
    sub: claims.sub,
    email: claims.email,
    name,
    pictureUrl: claims.picture,
    issuer: claims.iss,
    expiresAt: claims.exp,
    rawClaims: claims as Record<string, unknown>,
  };
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const tokens = TokenStore.getTokens();
    if (!tokens) {
      dispatch({ type: 'SET_UNAUTHENTICATED' });
      return;
    }
    if (TokenStore.isExpired(tokens)) {
      TokenStore.clear();
      dispatch({
        type: 'SET_UNAUTHENTICATED',
        payload: { error: { kind: 'SESSION_EXPIRED', message: 'Your session has expired. Please sign in again.' } },
      });
      return;
    }
    const decoded = decodeIdToken(tokens.idToken);
    if (!decoded.ok) {
      TokenStore.clear();
      dispatch({ type: 'SET_UNAUTHENTICATED' });
      return;
    }
    dispatch({ type: 'SET_AUTHENTICATED', payload: { user: claimsToAuthUser(decoded.claims) } });
  }, []);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const login = useCallback(async (options?: LoginStartOptions) => {
    dispatch({ type: 'CLEAR_ERROR' });
    await startLogin(options);
  }, []);

  const logout = useCallback((options?: { returnTo?: string }) => {
    TokenStore.clear();
    dispatch({ type: 'SET_UNAUTHENTICATED' });
    const logoutUrl = AuthService.buildLogoutUrl(options?.returnTo);
    window.location.assign(logoutUrl);
  }, []);

  const refreshFromStore = useCallback(async () => {
    await initialize();
  }, [initialize]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshFromStore,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
